import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X } from "lucide-react";
import type { SharingState } from "@desk/contracts";
import { Button } from "@/components/ui/button";
import { Input, Label } from "@/components/ui/input";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogTitle } from "@/components/ui/dialog";
import { ApiRequestError, describeError } from "@/data/api";
import { invalidateContainers, qk } from "@/data/cache";
import { useSharing } from "@/data/cloud";
import { useDesk } from "@/data/context";

const message = (e: unknown) => (e instanceof ApiRequestError && e.code !== "network" && e.message ? e.message : describeError(e));

/** Invite people to an area, see who's in it, remove people, or stop sharing. */
export function ShareDialog({ areaId, areaName, open, onOpenChange }: { areaId: string; areaName: string; open: boolean; onOpenChange: (o: boolean) => void }) {
  const { api } = useDesk();
  const qc = useQueryClient();
  const { data } = useSharing();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const shared = data?.areas.find((a) => a.areaId === areaId);
  const members = shared?.members.filter((m) => m.role !== "owner") ?? [];

  const act = async (fn: () => Promise<SharingState>, after?: () => void) => {
    setBusy(true);
    setError(null);
    try {
      qc.setQueryData(qk.sharing, await fn());
      after?.();
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px]">
        <DialogBody>
          <DialogTitle>Share {areaName}</DialogTitle>
          <DialogDescription>People you invite can see and edit everything in this area. Your other areas stay private.</DialogDescription>
          <form
            className="flex items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              void act(
                () => api.shareArea(areaId, email.trim()),
                () => {
                  toast(`Invited ${email.trim()}`);
                  setEmail("");
                },
              );
            }}
          >
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Label htmlFor="share-email">Email</Label>
              <Input id="share-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" spellCheck={false} autoFocus />
            </div>
            <Button type="submit" variant="default" disabled={busy || !email.includes("@")}>
              Invite
            </Button>
          </form>
          {error && (
            <p role="alert" className="rounded-md bg-urgent-soft px-3 py-2 text-[13px]">
              {error}
            </p>
          )}
          {members.length > 0 && (
            <ul className="flex flex-col divide-y divide-border/70 rounded-md border border-border/70">
              {members.map((m) => (
                <li key={m.email} className="flex items-center gap-3 px-3 py-2">
                  <span className="min-w-0 flex-1 truncate text-[13.5px]">{m.email}</span>
                  {m.status === "invited" && <span className="text-[12.5px] text-muted-foreground">Invited</span>}
                  <Button
                    size="icon-sm"
                    variant="quiet"
                    aria-label={`Remove ${m.email}`}
                    disabled={busy}
                    onClick={() => void act(() => api.removeMember(areaId, m.email), () => void invalidateContainers(qc))}
                  >
                    <X />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </DialogBody>
        <DialogFooter>
          {shared && (
            <Button variant="ghost" className="mr-auto text-urgent" disabled={busy} onClick={() => setConfirmStop(true)}>
              Stop sharing…
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
      <Dialog open={confirmStop} onOpenChange={setConfirmStop}>
        <DialogContent className="max-w-[420px]">
          <DialogBody>
            <DialogTitle>Stop sharing {areaName}?</DialogTitle>
            <DialogDescription>Everyone else loses it from their devices. You keep everything in it.</DialogDescription>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmStop(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() =>
                void act(
                  () => api.unshareArea(areaId),
                  () => {
                    setConfirmStop(false);
                    onOpenChange(false);
                    toast(`Stopped sharing ${areaName}`);
                  },
                )
              }
            >
              Stop sharing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
